export const GA_MEASUREMENT_ID = "G-5QQNGFF60X";
export const ANALYTICS_CONSENT_KEY = "orion.analytics-consent.v1";

const CONSENT_GRANTED = "granted";
const CONSENT_DENIED = "denied";

function getWindow() {
  return typeof window === "undefined" ? null : window;
}

function getDocument() {
  return typeof document === "undefined" ? null : document;
}

function ensureGtagQueue() {
  const browserWindow = getWindow();
  if (!browserWindow) return null;

  browserWindow.dataLayer = browserWindow.dataLayer || [];
  browserWindow.gtag = browserWindow.gtag || function gtag() {
    browserWindow.dataLayer.push(arguments);
  };

  return browserWindow.gtag;
}

export function initializeAnalyticsConsent() {
  const gtag = ensureGtagQueue();
  if (!gtag) return;

  gtag("consent", "default", {
    ad_storage: CONSENT_DENIED,
    ad_user_data: CONSENT_DENIED,
    ad_personalization: CONSENT_DENIED,
    analytics_storage: CONSENT_DENIED,
    wait_for_update: 500,
  });
}

export function readAnalyticsConsent() {
  const browserWindow = getWindow();
  if (!browserWindow) return null;

  const storedValue = browserWindow.localStorage.getItem(ANALYTICS_CONSENT_KEY);
  return storedValue === CONSENT_GRANTED || storedValue === CONSENT_DENIED
    ? storedValue
    : null;
}

export function saveAnalyticsConsent(value) {
  const browserWindow = getWindow();
  if (!browserWindow) return;

  browserWindow.localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
}

function configureAnalytics() {
  const browserWindow = getWindow();
  const gtag = ensureGtagQueue();
  if (!browserWindow || !gtag) return;

  browserWindow[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  gtag("consent", "update", {
    ad_storage: CONSENT_DENIED,
    ad_user_data: CONSENT_DENIED,
    ad_personalization: CONSENT_DENIED,
    analytics_storage: CONSENT_GRANTED,
  });
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    send_page_view: false,
  });
}

export function enableAnalytics() {
  const browserDocument = getDocument();
  if (!browserDocument) return;

  configureAnalytics();

  if (browserDocument.getElementById("orion-ga4-script")) return;

  const script = browserDocument.createElement("script");
  script.id = "orion-ga4-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  browserDocument.head.appendChild(script);
}

function deleteAnalyticsCookies() {
  const browserDocument = getDocument();
  const browserWindow = getWindow();
  if (!browserDocument || !browserWindow) return;

  const cookieNames = browserDocument.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter((name) => name === "_ga" || name === "_gid" || name === "_gat" || name.startsWith("_ga_"));
  const rootDomain = browserWindow.location.hostname.replace(/^www\./, "");

  cookieNames.forEach((name) => {
    browserDocument.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    browserDocument.cookie = `${name}=; Max-Age=0; path=/; domain=.${rootDomain}; SameSite=Lax`;
  });
}

export function disableAnalytics() {
  const browserWindow = getWindow();
  const browserDocument = getDocument();
  const gtag = ensureGtagQueue();
  if (!browserWindow || !browserDocument || !gtag) return;

  browserWindow[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  gtag("consent", "update", {
    ad_storage: CONSENT_DENIED,
    ad_user_data: CONSENT_DENIED,
    ad_personalization: CONSENT_DENIED,
    analytics_storage: CONSENT_DENIED,
  });
  browserDocument.getElementById("orion-ga4-script")?.remove();
  deleteAnalyticsCookies();
}

export function trackPageView(path) {
  const browserWindow = getWindow();
  const browserDocument = getDocument();
  if (!browserWindow || !browserDocument || readAnalyticsConsent() !== CONSENT_GRANTED) return;

  const gtag = ensureGtagQueue();
  gtag("event", "page_view", {
    page_location: browserWindow.location.href,
    page_path: path,
    page_title: browserDocument.title,
  });
}

export function trackEvent(eventName, parameters = {}) {
  if (readAnalyticsConsent() !== CONSENT_GRANTED) return;

  const gtag = ensureGtagQueue();
  if (!gtag) return;

  gtag("event", eventName, parameters);
}

